// Formatting helpers shared across the viewer.

const HEX = "0123456789abcdef";

export function bytesToHex(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]!;
    out += HEX[b >> 4]! + HEX[b & 0xf]!;
  }
  return out;
}

export async function sha256Hex(data: ArrayBuffer | Uint8Array): Promise<string> {
  // Normalize to a BufferSource SubtleCrypto accepts. The Uint8Array branch
  // copies into a fresh ArrayBuffer to satisfy the strict typing (the source
  // could in principle be a SharedArrayBuffer view).
  const view: BufferSource =
    data instanceof Uint8Array ? new Uint8Array(data).buffer : data;
  const digest = await crypto.subtle.digest("SHA-256", view);
  return bytesToHex(digest);
}

// Slack ts is "<seconds>.<microseconds>", a float-like string.
export function tsToDate(ts: string): Date {
  return new Date(parseFloat(ts) * 1000);
}

export function tsToEpochMs(ts: string): number {
  return parseFloat(ts) * 1000;
}

const tsShortFmt = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});

const tsLongFmt = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  timeZoneName: "short",
});

const tsDayFmt = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
});

export const formatTsShort = (ts: string): string => tsShortFmt.format(tsToDate(ts));
export const formatTsLong = (ts: string): string => tsLongFmt.format(tsToDate(ts));
export const formatTsDay = (ts: string): string => tsDayFmt.format(tsToDate(ts));

// Stable day key for grouping consecutive messages by date.
export function tsDayKey(ts: string): string {
  const d = tsToDate(ts);
  // Use local-time YYYY-MM-DD so day separators reflect the reader's wall clock.
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function shortHash(hex: string, n = 12): string {
  if (!hex) return "";
  return hex.length <= n ? hex : `${hex.slice(0, n)}…`;
}

// Coarse "X minutes/hours/days ago" formatter for the persisted-cards
// section on the upload screen. Not exposed in conversation views —
// those use absolute timestamps for evidence-handling rigor.
export function relativeTime(ms: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.floor(day / 30);
  return `${mo} month${mo === 1 ? "" : "s"} ago`;
}
